import { Button, Typography } from '@mui/material';
import { alpha, styled } from '@mui/material/styles';
import cn from 'classnames';
import DetectRTC from 'detectrtc';
import { useMemo } from 'react';
import Iconify from 'src/components/iconify';
import { useGatheringPermissions } from 'src/hooks/useGatheringPermissions';
import { useLivekitRTCState } from 'src/provider/VoiceChannel/livekit/useLivekitRTCState';
import { extractColor } from 'src/utils/utils';

export type ActionsButtonType = {
  title: string;
  icon: string;
  onClick: VoidFunction;
  state?: 'On' | 'Off';
  color:
    | 'error'
    | 'inherit'
    | 'primary'
    | 'secondary'
    | 'success'
    | 'info'
    | 'warning';
  alert: 'not-present' | 'not-granted' | false;
};

export const VideoActionsButtons = () => {
  const { audioPermission, videoPermission, hasCamera, hasMicrophone } =
    useGatheringPermissions();

  const { preConnectionOptions, setPreConnectionOptions } =
    useLivekitRTCState();

  const handleMuteMicrophone = async () => {
    if (!hasMicrophone) {
      return;
    }
    setPreConnectionOptions({ micOn: !preConnectionOptions.micOn });
  };

  const handleActiveVideo = async () => {
    if (!hasCamera) {
      return;
    }
    setPreConnectionOptions({ cameraOn: !preConnectionOptions.cameraOn });
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const actionButtons: ActionsButtonType[] = useMemo(
    () => [
      {
        title: 'microfone',
        icon:
          hasMicrophone && preConnectionOptions.micOn
            ? 'tabler:microphone'
            : 'tabler:microphone-off',
        state: hasMicrophone && preConnectionOptions.micOn ? 'On' : 'Off',
        onClick: handleMuteMicrophone,
        color:
          hasMicrophone && preConnectionOptions.micOn ? 'inherit' : 'error',
        alert: DetectRTC.hasMicrophone
          ? audioPermission === 'granted'
            ? false
            : 'not-granted'
          : 'not-present',
      },
      {
        title: 'camera',
        icon:
          hasCamera && preConnectionOptions.cameraOn
            ? 'tabler:video'
            : 'tabler:video-off',
        state: hasCamera && preConnectionOptions.cameraOn ? 'On' : 'Off',
        onClick: handleActiveVideo,
        color: hasCamera && preConnectionOptions.cameraOn ? 'inherit' : 'error',
        alert: DetectRTC.hasWebcam
          ? videoPermission === 'granted'
            ? false
            : 'not-granted'
          : 'not-present',
      },
    ],
    [
      preConnectionOptions.micOn,
      preConnectionOptions.cameraOn,
      audioPermission,
      videoPermission,
      hasMicrophone,
      hasCamera,
    ],
  );

  return (
    <ActionsCamButtonsContainer>
      <CustomButtonsContainer>
        {actionButtons.map((button) => (
          <StyledButton
            key={button.title}
            color={button.color}
            variant="outlined"
            onClick={button.onClick}
            className={cn({ hasAlert: !!button.alert })}
            disabled={!!button.alert}
          >
            {!!button.alert && (
              <StyledAlert className={button.alert}>
                <Iconify icon="tabler:alert-small" color="white" />
              </StyledAlert>
            )}
            <Iconify icon={button.icon} />
            <Typography variant="body1" color="text.secondary">
              {button.alert ? 'Off' : button.state}
            </Typography>
          </StyledButton>
        ))}
      </CustomButtonsContainer>
    </ActionsCamButtonsContainer>
  );
};

const CustomButtonsContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
}));

const ActionsCamButtonsContainer = styled('div')(({ theme }) => ({
  position: 'absolute',
  display: 'flex',
  bottom: 0,
  justifyContent: 'center',
  zIndex: 2,
  transform: 'scale(0.8)',
  [theme.breakpoints.up('md')]: {
    bottom: theme.spacing(2),
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  position: 'relative',
  justifyContent: 'flex-start',
  width: theme.spacing(11),
  minWidth: 'unset',
  height: 36,
  gap: theme.spacing(1),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(extractColor(theme.palette.background.default), 0.9),
  fontWeight: 300,
  padding: theme.spacing(1, 2),
  border: '1px solid transparent',
  '&.hasAlert': {
    backgroundColor: alpha(extractColor(theme.palette.error.main), 0.08),
    border: `1px solid ${theme.palette.error.main}`,
  },
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    borderColor: 'transparent',
  },
  [theme.breakpoints.up('md')]: {
    height: 40,
  },
}));

const StyledAlert = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(-1),
  right: theme.spacing(-1),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 20,
  height: 20,
  borderRadius: '50%',

  '&.not-granted': {
    backgroundColor: theme.palette.warning.main,
  },
  '&.not-present': {
    backgroundColor: theme.palette.error.main,
  },
}));
